import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Ship, Plane, Truck, TrainFront, Package } from 'lucide-react';
import {
  freightLegCatalogService,
  type CatalogFreightLeg,
} from '@/features/master-data/services/freight-leg-catalog-service';
import type { TransportMode } from '../types/import-tracking.types';

const MODE_ICON: Record<TransportMode, React.ElementType> = {
  SEA: Ship,
  AIR: Plane,
  LAND: Truck,
  RAIL: TrainFront,
  MULTIMODAL: Package,
};

interface LegCatalogPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (leg: CatalogFreightLeg) => void;
}

export function LegCatalogPicker({ open, onOpenChange, onPick }: LegCatalogPickerProps) {
  const [legs, setLegs] = useState<CatalogFreightLeg[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    freightLegCatalogService
      .list({
        q: search || undefined,
        mode: (modeFilter || undefined) as TransportMode | undefined,
      })
      .then((data) => {
        if (!cancelled) setLegs(data);
      })
      .catch(() => {
        if (!cancelled) setLegs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, search, modeFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Buscar tramo en el catálogo</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar origen o destino..."
              className="pl-9"
            />
          </div>
          <Select
            value={modeFilter || '__all__'}
            onValueChange={(v) => setModeFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los modos</SelectItem>
              <SelectItem value="SEA">Marítimo</SelectItem>
              <SelectItem value="AIR">Aéreo</SelectItem>
              <SelectItem value="LAND">Terrestre</SelectItem>
              <SelectItem value="RAIL">Ferroviario</SelectItem>
              <SelectItem value="MULTIMODAL">Multimodal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando catálogo...</div>
          ) : legs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Sin tramos en el catálogo.</p>
              <p className="text-xs mt-1">Crea tramos en Datos Maestros &gt; Catálogo de Tramos</p>
            </div>
          ) : (
            legs.map((leg) => {
              const Icon = MODE_ICON[leg.transportMode] ?? Package;
              return (
                <button
                  type="button"
                  key={leg.id}
                  onClick={() => onPick(leg)}
                  className="w-full text-left rounded-lg border p-3 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Icon className="w-5 h-5 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          {leg.origin} → {leg.destination}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{leg.transportMode}</span>
                          {leg.provider && (
                            <>
                              <span>·</span>
                              <span>{leg.provider.name}</span>
                            </>
                          )}
                          {leg.estimatedDays != null && (
                            <>
                              <span>·</span>
                              <span>{leg.estimatedDays} días</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-xs tabular-nums whitespace-nowrap">
                      {leg.ratePerCbm > 0 && <div>${leg.ratePerCbm.toFixed(2)}/CBM</div>}
                      {leg.ratePerKg > 0 && <div>${leg.ratePerKg.toFixed(2)}/KG</div>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
